import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link } from 'react-router-dom';

// Icons
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GroupsIcon from '@mui/icons-material/Groups';
import FactoryIcon from '@mui/icons-material/Factory';
import FlightIcon from '@mui/icons-material/Flight';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BoltIcon from '@mui/icons-material/Bolt';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ComputerIcon from '@mui/icons-material/Computer';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScienceIcon from '@mui/icons-material/Science';
import MemoryIcon from '@mui/icons-material/Memory';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedIcon from '@mui/icons-material/Verified';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const Home = () => {
  return (
    <Box>
      {/* 1. HERO SECTION */}
      <Box
        sx={{
          minHeight: '90vh', // Use minHeight to allow growth
          width: '100%',
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.6)), url(/images/cnc_hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll', // 'scroll' is often better for mobile perf than fixed
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          py: { xs: 8, md: 0 }, // Add flexible padding on mobile
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ animation: 'fadeInUp 1s ease-out', mt: { xs: 4, md: 0 } }}>
            <Typography
              variant="h1"
              component="h1"
              fontWeight="900"
              sx={{
                fontSize: { xs: '2.5rem', md: '4.5rem' }, // Slightly larger mobile size but still manageable
                lineHeight: { xs: 1.2, md: 1.1 },
                textShadow: '2px 2px 10px rgba(0,0,0,0.8)',
                letterSpacing: '1px',
                mb: 3
              }}
            >
              PRECISION ENGINEERING <br />
              <span style={{ color: '#4fc3f7' }}>REDEFINED</span>
            </Typography>

            <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 300, opacity: 0.9, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              AYDA INDUSTRIES PVT LTD
            </Typography>

            <Typography variant="h6" paragraph sx={{ mb: 6, maxWidth: '800px', mx: 'auto', opacity: 0.9, lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Delivering world-class machined components with advanced VMC technology.
              Your trusted partner for high-accuracy manufacturing solutions across Automotive, Aerospace, and Industrial sectors.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  bgcolor: '#0288d1',
                  '&:hover': { bgcolor: '#01579b', transform: 'scale(1.05)' },
                  transition: 'all 0.3s',
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Request Quote
              </Button>
              <Button
                component={Link}
                to="/services"
                variant="outlined"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: '#4fc3f7',
                    color: '#4fc3f7',
                    bgcolor: 'rgba(255,255,255,0.05)'
                  },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Our Capabilities
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* 2. STATS SECTION (ANIMATED) */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
            spacing={4}
            justifyContent="space-around"
            alignItems="center"
          >
            {[
              { label: 'Components Delivered', value: '1M+' },
              { label: 'Client Retention Rate', value: '98%' },
              { label: 'Years of Experience', value: '15+' },
              { label: 'Awards Won', value: '50+' }
            ].map((stat, index) => (
              <Box
                key={index}
                textAlign="center"
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* 2.5. TRUSTED BY / CERTIFICATIONS (NEW) */}
      <Box sx={{ bgcolor: '#eeeeee', py: 4, borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" spacing={4}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              TRUSTED BY INDUSTRY LEADERS:
            </Typography>
            <Stack direction="row" spacing={6} sx={{ opacity: 0.5, overflowX: 'auto', width: '100%', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {/* Placeholders for textual logos to simulate brands */}
              {['AERO-DYNAMICS', 'TECH-AUTO', 'MED-CORE', 'POWER-GRID', 'FUTURE-SYSTEMS'].map((brand) => (
                <Typography key={brand} variant="h6" fontWeight="900" sx={{ color: '#000', minWidth: 'fit-content' }}>{brand}</Typography>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* 3. EXTENDED INTRO / ABOUT SECTION (ANIMATED) */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              position="relative"
              component={motion.div}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="overline" color="primary" fontWeight="bold" fontSize="1rem" letterSpacing={2}>
                WHO WE ARE
              </Typography>
              <Typography variant="h3" component="h2" gutterBottom fontWeight="800" sx={{ mb: 3 }}>
                Advanced Manufacturing for a <span style={{ color: '#0288d1' }}>Modern World</span>
              </Typography>
              <Box sx={{ width: 80, height: 6, bgcolor: '#0288d1', mb: 4, borderRadius: 2 }} component={motion.div} initial={{ width: 0 }} whileInView={{ width: 80 }} transition={{ duration: 1, delay: 0.5 }} />

              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                AYDA INDUSTRIES is a premier manufacturer specializing in high-precision machined components.
                We leverage cutting-edge Vertical Machining Center (VMC) technology to produce parts that meet rigorous industrial standards.
              </Typography>

              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                From rapid prototyping to mass production, our facility is equipped to handle complex profiles with exceptional repeatability.
                We view every project as a partnership, ensuring that our engineering excellence translates directly into your product's success.
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {[
                  'ISO 9001:2015 Certified',
                  'Lean Manufacturing Principles',
                  '24/7 Production Capability',
                  'End-to-End Traceability'
                ].map((item, i) => (
                  <Grid item xs={6} key={item}>
                    <Box
                      display="flex"
                      alignItems="center"
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + (i * 0.1) }}
                    >
                      <VerifiedUserIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2" fontWeight="600">{item}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 5 }}>
                <Button component={Link} to="/about" variant="text" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                  Read More About Us &rarr;
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={10}
              sx={{ overflow: 'hidden', borderRadius: 4, transform: 'rotate(2deg)', transition: '0.3s', '&:hover': { transform: 'rotate(0deg)' } }}
              component={motion.div}
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 2 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img src="/images/factory.png" alt="Factory Floor" style={{ width: '100%', height: 'auto', display: 'block' }} />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* 4. INDUSTRIES SERVED (ANIMATED) */}
      <Box sx={{ bgcolor: '#f5f5f5', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8} component={motion.div} initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="overline" color="text.secondary" fontWeight="bold" fontSize="1rem" letterSpacing={2}>
              MARKETS WE SERVE
            </Typography>
            <Typography variant="h3" fontWeight="800" sx={{ mt: 1 }}>
              Powering Key Industries
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
              Our versatile manufacturing capabilities allow us to deliver critical components for diverse high-tech sectors.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              { icon: <DriveEtaIcon sx={{ fontSize: 50 }} />, title: 'Automotive', desc: 'Precision engine components, transmission parts, and custom tooling fixtures.' },
              { icon: <FlightIcon sx={{ fontSize: 50 }} />, title: 'Aerospace', desc: 'Lightweight, high-tolerance parts machined from exotic alloys for flight systems.' },
              { icon: <MedicalServicesIcon sx={{ fontSize: 50 }} />, title: 'Medical', desc: 'Surgical instruments and implantable components requiring biocompatible materials.' },
              { icon: <BoltIcon sx={{ fontSize: 50 }} />, title: 'Energy & Power', desc: 'Robust components designed to withstand extreme pressure and environments.' },
            ].map((market, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  sx={{ height: '100%', borderRadius: 3, transition: '0.3s', '&:hover': { translateY: '-10px', boxShadow: 6 } }}
                >
                  <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      mb: 3,
                      opacity: 0.1
                    }}>
                      {/* background bubble */}
                    </Box>
                    <Box sx={{ color: '#0288d1', mb: 2, position: 'absolute', mt: 3 }}>
                      {market.icon}
                    </Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 8, mb: 2 }}>
                      {market.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {market.desc}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4.2. MATERIAL EXPERTISE (NEW MASSIVE ADDITION) */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={2}>MATERIALS</Typography>
              <Typography variant="h3" fontWeight="900" gutterBottom>
                Mastery Over Matter
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
                We don't just cut metal; we engineer material properties.
                Our tooling strategies are optimized for specific alloys to ensure structural integrity and surface finish.
              </Typography>

              <List sx={{ mt: 2 }}>
                {[
                  { title: 'Stainless Steel', desc: '304, 316L, 17-4PH for corrosion resistance.' },
                  { title: 'Aluminum Alloys', desc: '6061-T6, 7075 for lightweight strength.' },
                  { title: 'Titanium & Inconel', desc: 'Grade 5, 718 for extreme aerospace applications.' },
                  { title: 'Engineering Plastics', desc: 'Delrin, PEEK, Nylon for precision insulators.' }
                ].map((m, i) => (
                  <ListItem key={i} sx={{ px: 0, py: 2, borderBottom: '1px solid #eee' }}>
                    <ListItemText
                      primary={<Typography variant="h6" fontWeight="bold">{m.title}</Typography>}
                      secondary={<Typography variant="body2">{m.desc}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
              <Button variant="outlined" sx={{ mt: 4, px: 4, py: 1.5 }} component={Link} to="/services">
                View Material Data Sheets
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{
                    height: 300,
                    bgcolor: '#cfd8dc',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: '0.3s',
                    '&:hover': { transform: 'scale(0.98)' }
                  }}>
                    <img src="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=400&q=80" alt="Steel" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Box sx={{ position: 'absolute', bottom: 0, p: 2, width: '100%', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                      <Typography variant="h6" color="white" fontWeight="bold">Hard Metals</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ mt: { sm: 6 } }}>
                  <Box sx={{
                    height: 300,
                    bgcolor: '#cfd8dc',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: '0.3s',
                    '&:hover': { transform: 'scale(0.98)' }
                  }}>
                    <img src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=400&q=80" alt="Aluminum" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Box sx={{ position: 'absolute', bottom: 0, p: 2, width: '100%', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                      <Typography variant="h6" color="white" fontWeight="bold">Light Alloys</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 4.4. SMART MANUFACTURING (NEW MASSIVE ADDITION) */}
      <Box sx={{ bgcolor: '#212121', color: 'white', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
            <Typography variant="overline" color="#00e676" fontWeight="bold">INDUSTRY 4.0</Typography>
            <Typography variant="h3" fontWeight="bold">Smart Manufacturing Integration</Typography>
            <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.8, mt: 2 }}>
              We don't just rely on skill; we rely on data. Our facility is connected through an advanced IIoT ecosystem that monitors machine health, OEE, and production status in real-time.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { icon: <ComputerIcon sx={{ fontSize: 40, color: '#00e676' }} />, title: 'Real-Time Monitoring', desc: 'Live dashboards tracking spindle load and cycle times.' },
              { icon: <MemoryIcon sx={{ fontSize: 40, color: '#00e676' }} />, title: 'Predictive Maintenance', desc: 'AI-driven alerts preventing machine downtime.' },
              { icon: <AssessmentIcon sx={{ fontSize: 40, color: '#00e676' }} />, title: 'Digital Traceability', desc: 'QR code tracking for every batch from raw material to dispatch.' },
              { icon: <BoltIcon sx={{ fontSize: 40, color: '#00e676' }} />, title: 'Energy Efficiency', desc: 'Smart power management reducing carbon footprint.' }
            ].map((feat, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box sx={{
                  p: 4,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: 4,
                  height: '100%',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: '0.3s',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-5px)' }
                }}>
                  <Box sx={{ mb: 3 }}>{feat.icon}</Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{feat.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>{feat.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4.5. FEATURED CASE STUDIES (NEW ADDITION) */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
        <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
          <Typography variant="overline" color="primary" fontWeight="bold" fontSize="1rem" letterSpacing={2}>
            SUCCESS STORIES
          </Typography>
          <Typography variant="h3" fontWeight="800" gutterBottom>
            Engineering Challenges Solved
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            We take pride in turning complex problems into precision solutions. Here are a few examples of how we've helped our clients succeed.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {[
            {
              title: "Aerospace Turbine Prototype",
              cat: "AEROSPACE",
              challenge: "Machining complex airfoil shapes with difficult-to-cut Inconel alloy.",
              solution: "Utilized advanced 4-axis VMC strategies and custom ceramic tooling.",
              result: "Achieved 20% weight reduction and delivered within 10 days.",
              icon: <FlightIcon fontSize="large" color="secondary" />
            },
            {
              title: "EV Battery Housing",
              cat: "AUTOMOTIVE",
              challenge: "High-volume production requiring consistent thermal management features.",
              solution: "Designed reduced-setup fixtures and implemented automated probing.",
              result: "Zero defects across a 50,000 unit production run.",
              icon: <DriveEtaIcon fontSize="large" color="primary" />
            },
            {
              title: "Surgical Robot Arm",
              cat: "MEDICAL",
              challenge: "Micron-level tolerances for articulation points.",
              solution: "Temperature-controlled machining environment and 100% CMM inspection.",
              result: "Perfect assembly fitment first time right.",
              icon: <MedicalServicesIcon fontSize="large" color="success" />
            }
          ].map((study, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 4, borderTop: '6px solid #0288d1', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -10, right: -10, opacity: 0.1, transform: 'rotate(15deg) scale(2)' }}>
                  {study.icon}
                </Box>
                <Chip label={study.cat} color="primary" size="small" sx={{ mb: 2, fontWeight: 'bold' }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {study.title}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error">The Challenge:</Typography>
                  <Typography variant="body2" color="text.secondary">{study.challenge}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">Our Solution:</Typography>
                  <Typography variant="body2" color="text.secondary">{study.solution}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="success.main">The Result:</Typography>
                  <Typography variant="body2" color="text.secondary">{study.result}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* 5. DETAILED MANUFACTURING JOURNEY (REFACTORED) */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 12 }, overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 8, md: 12 }}>
            <Typography variant="overline" color="primary" fontWeight="bold" fontSize="1rem" letterSpacing={2}>
              HOW WE WORK
            </Typography>
            <Typography variant="h3" fontWeight="800" gutterBottom>
              Visualizing the Process
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Transparency is key. We follow a rigorous 6-step protocol to ensure every component meets the highest standards of precision.
            </Typography>
          </Box>

          <Stack spacing={0} sx={{ position: 'relative' }}>
            {/* Vertical Line for Desktop */}
            <Box sx={{
              position: 'absolute',
              left: { xs: 20, md: '50%' },
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: '#e0e0e0',
              transform: { md: 'translateX(-2px)' },
              zIndex: 0
            }} />

            {[
              {
                step: '01', title: 'Consultation & Feasibility',
                desc: 'We begin by analyzing your drawings (2D/3D) and technical requirements. Our engineers assess feasibility, suggest material optimizations, and provide a detailed cost estimate.',
                img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80'
              },
              {
                step: '02', title: 'CAD/CAM Programming',
                desc: 'Once approved, we convert designs into machine code using advanced CAM software. We simulate tool paths to ensure optimal cutting strategies and error-free machining.',
                img: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=800&q=80'
              },
              {
                step: '03', title: 'Material Preparation',
                desc: 'We source high-grade raw materials from certified suppliers. Every block or rod is inspected for material composition consistency before it hits the shop floor.',
                img: 'https://images.unsplash.com/photo-1616499370260-485b3e5ed653?auto=format&fit=crop&w=800&q=80'
              },
              {
                step: '04', title: 'High-Precision Machining',
                desc: 'The core action happens here. Our 3, 4, and 5-axis VMCs carve out complex geometries with micron-level precision, monitored by seasoned operators.',
                img: 'https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?auto=format&fit=crop&w=800&q=80'
              },
              {
                step: '05', title: 'Quality Assurance (CMM)',
                desc: 'Finished parts undergo rigorous inspection. We use CMM (Coordinate Measuring Machines), Vernier calipers, and gauges to verify every dimension against the blueprint.',
                img: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=800&q=80'
              },
              {
                step: '06', title: 'Finishing & Dispatch',
                desc: 'Parts are deburred, polished, anodized (if req), and securely packaged. We coordinate logistics to ensure on-time delivery anywhere in the world.',
                img: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80'
              }
            ].map((item, idx) => (
              <Box key={idx} sx={{ position: 'relative', mb: 8, display: 'flex', justifyContent: { xs: 'flex-start', md: idx % 2 === 0 ? 'flex-end' : 'flex-start' } }}>
                {/* Timeline Dot */}
                <Box sx={{
                  position: 'absolute',
                  left: { xs: 20, md: '50%' },
                  top: 0,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  border: '4px solid white',
                  boxShadow: 2,
                  transform: { xs: 'translateX(-10px)', md: 'translateX(-10px)' },
                  zIndex: 2
                }} />

                <Paper elevation={3} sx={{
                  width: { xs: 'calc(100% - 60px)', md: '45%' },
                  ml: { xs: 8, md: 0 },
                  mr: { md: idx % 2 === 0 ? 'auto' : 0 },
                  ...(idx % 2 === 0 ? { mr: { md: '55%' } } : { ml: { md: '55%' } }),
                  overflow: 'hidden',
                  borderRadius: 3,
                  transition: '0.3s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 }
                }}>
                  <Box sx={{ height: 200, overflow: 'hidden' }}>
                    <img src={item.img} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h2" color="rgba(0,0,0,0.1)" fontWeight="900" sx={{ fontSize: '3rem', lineHeight: 1, mb: -2 }}>
                      {item.step}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ position: 'relative', zIndex: 1, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* 5.5. TECHNOLOGY & INFRASTRUCTURE (NEW ADDITION) */}
      <Box sx={{ bgcolor: '#263238', color: 'white', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="#4fc3f7" fontWeight="bold" fontSize="1rem" letterSpacing={2}>
                OUR BACKBONE
              </Typography>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                Technology That Drives Precision
              </Typography>
              <Typography variant="body1" paragraph sx={{ opacity: 0.8, fontSize: '1.1rem', lineHeight: 1.8 }}>
                To deliver world-class components, you need world-class infrastructure. We believe in continuous investment in the latest manufacturing technologies to stay ahead of the curve.
              </Typography>

              <Stack spacing={3} sx={{ mt: 4 }}>
                <Box display="flex">
                  <MemoryIcon sx={{ color: '#4fc3f7', fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Advanced VMC Fleet</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Equipped with high-speed spindles and rigid structural designs for superior surface finishes.
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex">
                  <AssessmentIcon sx={{ color: '#4fc3f7', fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Quality Lab</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Comprehensive inspection facility featuring CMM, profile projectors, and digital gauging.
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex">
                  <ComputerIcon sx={{ color: '#4fc3f7', fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Digital Manufacturing</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Integrated CAD/CAM workflows ensuring seamless transition from design to part.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{ transform: { xs: 'none', sm: 'translateY(30px)' }, mb: { xs: 2, sm: 0 } }}>
                  <img src="https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="CNC Machine" style={{ width: '100%', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Quality Control" style={{ width: '100%', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* 5.75. QUALITY ASSURANCE ECOSYSTEM (NEW ADDITION) */}
      <Box sx={{ bgcolor: '#fafafa', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <img src="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?auto=format&fit=crop&w=800&q=80" alt="Quality Inspection" style={{ width: '100%', borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }} />
                <Box sx={{
                  position: 'absolute', bottom: -30, left: -30,
                  bgcolor: '#0288d1', color: 'white', p: 4, borderRadius: 2,
                  boxShadow: 4, maxWidth: 300
                }}>
                  <Typography variant="h6" fontWeight="bold">ISO 9001:2015</Typography>
                  <Typography variant="body2">Certified Manufacturing Processes ensuring global compliance.</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="primary" fontWeight="bold">PRECISION GUARANTEED</Typography>
              <Typography variant="h3" fontWeight="800" sx={{ mb: 3 }}>
                Where Accuracy Meets Accountability
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                At AYDA, quality isn't just a final step; it's embedded in every stage of production. From raw material verification to final surface analysis, we leave nothing to chance.
              </Typography>

              <List>
                {[
                  { icon: <VerifiedIcon color="success" />, title: 'Incoming Material Inspection', desc: 'Ultrasonic testing and chemical composition analysis.' },
                  { icon: <VerifiedIcon color="success" />, title: 'In-Process Probing', desc: 'Real-time Renishaw probing for automated offset corrections.' },
                  { icon: <VerifiedIcon color="success" />, title: 'Final Dimensional Report', desc: 'Detailed inspection reports provided with every shipment.' },
                  { icon: <VerifiedIcon color="success" />, title: 'Calibration Standards', desc: 'All instruments calibrated annually by NABL accredited labs.' }
                ].map((item, i) => (
                  <ListItem key={i} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>}
                      secondary={item.desc}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ bgcolor: '#1a237e', color: 'white', py: 12, position: 'relative', overflow: 'hidden' }}>
        {/* Background Pattern Overlay */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          opacity: 0.2
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" color="#4fc3f7" fontWeight="bold" fontSize="1rem">
              THE AYDA ADVANTAGE
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" color="white">
              Why Leaders Trust Us
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { icon: <PrecisionManufacturingIcon fontSize="large" />, title: 'Advanced Technology', desc: 'State-of-the-art VMC/CNC machines for complex geometries.' },
              { icon: <VerifiedUserIcon fontSize="large" />, title: 'Zero-Defect Policy', desc: 'Strict Quality Assurance protocols ensuring every micron counts.' },
              { icon: <SpeedIcon fontSize="large" />, title: 'Rapid Turnaround', desc: 'Optimized workflows to meet tight deadlines without quality compromise.' },
              { icon: <EngineeringIcon fontSize="large" />, title: 'Expert Team', desc: 'Seasoned engineers dedicated to process optimization.' },
              { icon: <GroupsIcon fontSize="large" />, title: 'Client Partnership', desc: 'We build lasting relationships by delivering tailored solutions.' },
              { icon: <FactoryIcon fontSize="large" />, title: 'Scalable Capacity', desc: 'From prototypes to large-scale batch manufacturing.' },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    color: 'white',
                    transition: '0.3s',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-5px)' }
                  }}
                >
                  <Box sx={{ color: '#4fc3f7', mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.7, lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 6.5. SUSTAINABILITY (NEW ADDITION) */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 4, p: { xs: 4, md: 8 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -50, right: -50, opacity: 0.1, color: 'success.main' }}>
            <NaturePeopleIcon sx={{ fontSize: 400 }} />
          </Box>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight="bold" gutterBottom color="success.dark">
                Committed to a Greener Future
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'success.dark' }}>
                At AYDA INDUSTRIES, we understand that manufacturing has a responsibility to the planet. We are actively implementing eco-friendly practices across our operations.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                  'Coolant Recycling Systems',
                  'Energy-Efficient Servo Motors',
                  'Scrap Material Recycling',
                  'Paperless Shop Floor Initiatives'
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box display="flex" alignItems="center">
                      <ScienceIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="body2" fontWeight="bold" color="success.dark">{item}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Paper elevation={0} sx={{ bgcolor: 'white', p: 3, borderRadius: '50%', width: 200, height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
                <Typography variant="h3" fontWeight="900" color="success.main">100%</Typography>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">COMMITMENT</Typography>
                <Typography variant="caption" color="text.secondary">TO SUSTAINABILITY</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* 6.75 GLOBAL REACH (NEW ADDITION) */}
      <Box sx={{ bgcolor: '#1a237e', color: 'white', py: 12, position: 'relative' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="#4fc3f7" fontWeight="bold">GLOBAL FOOTPRINT</Typography>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Exporting Excellence Worldwide
              </Typography>
              <Typography variant="body1" paragraph sx={{ opacity: 0.8, fontSize: '1.1rem' }}>
                Our components drive machines in over 15 countries. We have mastered the logistics of international trade, ensuring seamless delivery whether you are in Europe, North America, or Asia.
              </Typography>

              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="h2" fontWeight="bold" sx={{ color: '#4fc3f7' }}>15+</Typography>
                  <Typography variant="subtitle2">Countries Served</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h2" fontWeight="bold" sx={{ color: '#4fc3f7' }}>48h</Typography>
                  <Typography variant="subtitle2">Quote Response Time</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', p: 2, border: '1px dashed rgba(255,255,255,0.3)', borderRadius: 4 }}>
                <PublicIcon sx={{ fontSize: { xs: 200, md: 400 }, opacity: 0.1, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                {/* Abstract Map Dots or List */}
                <Grid container spacing={2} justifyContent="center" sx={{ position: 'relative', zIndex: 1 }}>
                  {['USA', 'Germany', 'Japan', 'UK', 'UAE', 'Canada'].map(c => (
                    <Grid item key={c}>
                      <Chip label={c} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }} avatar={<Avatar sx={{ bgcolor: 'white' }}><PublicIcon color="primary" sx={{ fontSize: 16 }} /></Avatar>} />
                    </Grid>
                  ))}
                </Grid>
                <Typography align="center" variant="caption" display="block" sx={{ mt: 10, opacity: 0.6 }}>
                  * Simplified representation of our export network
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 6.85. IMAGE GALLERY (NEW ADDITION) */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box textAlign="center" mb={8}>
          <Typography variant="overline" color="primary" fontWeight="bold">PORTFOLIO</Typography>
          <Typography variant="h3" fontWeight="bold">Recent Projects</Typography>
          <Typography variant="body1" color="text.secondary">
            A visual tour of our most recent high-precision deliveries.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {[
            { img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Precision Gear Shaft' },
            { img: 'https://images.unsplash.com/photo-1590959651373-a3db0f38a961?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Aero Engine Part' },
            { img: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Hydraulic Manifold' },
            { img: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Titanium Implant' }, // Wide
            { img: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', title: 'Micro Machined Component' },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} md={i === 3 ? 8 : 4} key={i}>
              <Paper elevation={2} sx={{
                borderRadius: 4, overflow: 'hidden', height: 300, position: 'relative',
                '&:hover img': { transform: 'scale(1.1)' },
                '&:hover .caption': { opacity: 1, transform: 'translateY(0)' }
              }}>
                <img src={item.img} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} />
                <Box className="caption" sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                  p: 3,
                  opacity: 0,
                  transform: 'translateY(20px)',
                  transition: '0.3s'
                }}>
                  <Typography color="white" fontWeight="bold" variant="subtitle1">{item.title}</Typography>
                  <Typography color="rgba(255,255,255,0.7)" variant="caption">Project #{1000 + i}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{
              height: 300,
              bgcolor: '#0288d1',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer',
              transition: '0.3s',
              boxShadow: 3,
              '&:hover': { bgcolor: '#01579b', transform: 'translateY(-5px)' }
            }} component={Link} to="/services">
              <CameraAltIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
              <Typography variant="h5" fontWeight="bold">View Full Gallery</Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="h3" align="center" fontWeight="bold" gutterBottom sx={{ mb: 8 }}>
          What Our Clients Say
        </Typography>
        <Grid container spacing={4}>
          {[
            { text: "AYDA Industries has been our go-to partner for complex machining. Their attention to detail and ability to hold tight tolerances is unmatched.", author: "Production Manager", company: "Automotive Tier 1 Supplier" },
            { text: "They turned our prototype around in record time without cutting corners on quality. A truly professional team.", author: "R&D Lead", company: "Aerospace Startup" },
            { text: "Reliable, efficient, and cost-effective. The quality of components we receive is consistently excellent.", author: "Supply Chain Director", company: "Industrial Equipment Mfg" },
          ].map((t, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card sx={{ height: '100%', p: 3, borderRadius: 4, boxShadow: 3, position: 'relative' }}>
                <FormatQuoteIcon sx={{ fontSize: 40, color: 'text.disabled', position: 'absolute', top: 20, right: 20 }} />
                <CardContent>
                  <Box display="flex" mb={2}>
                    {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                  </Box>
                  <Typography variant="body1" paragraph fontStyle="italic" sx={{ mb: 3 }}>
                    "{t.text}"
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>{t.author[0]}</Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">{t.author}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.company}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>


      {/* 8. FAQ SECTION (NEW) */}
      <Box sx={{ bgcolor: '#fafafa', py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h3" align="center" fontWeight="bold" mb={6}>
            Frequently Asked Questions
          </Typography>
          {[
            { q: "What materials can you machine?", a: "We work with a wide range of materials including Stainless Steel (304, 316), Aluminum (6061, 7075), Brass, Titanium, Inconel, and Engineering Plastics like Delrin and Nylon." },
            { q: "What are your tolerance capabilities?", a: "Our advanced VMC machines can hold tolerances as tight as +/- 0.005mm (5 microns), depending on the part geometry and material." },
            { q: "Do you handle low-volume prototypes?", a: "Yes, we handle everything from single-piece prototypes to high-volume production runs of 100,000+ units." },
            { q: "What file formats do you accept?", a: "We prefer standard CAD formats such as STEP (.stp), IGES (.igs), and DXF for 2D drawings. SolidWorks and AutoCAD native files are also accepted." },
          ].map((faq, i) => (
            <Accordion key={i} sx={{ mb: 2, boxShadow: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                <Typography variant="h6" fontWeight="600">{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* 9. CTA SECTION */}
      <Box sx={{ py: 12, bgcolor: '#0288d1', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Ready to Start Your Project?
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 6, opacity: 0.9 }}>
            Partner with AYDA INDUSTRIES for high-precision manufacturing solutions.
            Let's discuss how we can bring your designs to reality with superior quality.
          </Typography>
          <Stack direction="row" spacing={3} justifyContent="center">
            <Button component={Link} to="/contact" variant="contained" color="secondary" size="large" sx={{ px: 6, py: 2, fontSize: '1.2rem', borderRadius: 2 }}>
              Get a Quote
            </Button>
            <Button component={Link} to="/contact" variant="outlined" color="inherit" size="large" sx={{ px: 6, py: 2, fontSize: '1.2rem', borderRadius: 2 }}>
              Contact Us
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
